import type { Behavior } from '@capture/shared';

interface Props {
  behavior: Extract<Behavior, { kind: 'parse' }>;
  onChange: (b: Behavior) => void;
  fieldNames: string[];
  advanced: boolean;
}

const COMPONENTS = ['last', 'first', 'middle'] as const;

/**
 * Field-detail editor for a parse behavior: pick the parser, choose which side
 * the operator edits (parts vs. composite), and map each component to a sibling
 * field. Styling is inherited from `.behavior-card__body` (design tokens) the
 * same way the sibling DisplayBody / TransformBody editors are.
 */
export function ParseBody({ behavior, onChange, fieldNames }: Props) {
  const setTarget = (component: string, fieldName: string) =>
    onChange({ ...behavior, targets: { ...behavior.targets, [component]: fieldName } });

  return (
    <div className="parse-body">
      <label>
        Parser
        <select value={behavior.parser} onChange={(e) => onChange({ ...behavior, parser: e.target.value })}>
          <option value="name">name</option>
        </select>
      </label>

      <label>
        Edit mode
        <select
          value={behavior.editMode}
          onChange={(e) => onChange({ ...behavior, editMode: e.target.value as 'parts' | 'composite' })}
        >
          <option value="parts">parts (operator edits the components)</option>
          <option value="composite">composite (operator edits the whole value)</option>
        </select>
      </label>

      {COMPONENTS.map((component) => (
        <label key={component}>
          {component}
          <select value={behavior.targets[component] ?? ''} onChange={(e) => setTarget(component, e.target.value)}>
            <option value="">— field —</option>
            {fieldNames.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
}
